import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductBase } from '@vestico/model';

import { Button, Spinner, SpinnerPositioning, SpinnerSize, SuccessCheck, useProductName } from '@vestico/shared';
import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { AddToBagStatus, AddToBagVariant } from '../hooks/useAddToBagActions';
import { AddToBagVariantOption } from './AddToBagVariantOption';
import style from './AddToBagVariantSelect.module.css';

export interface AddToBagVariantSelectProps {
  variants: AddToBagVariant[] | undefined;
  status: AddToBagStatus;
  addToBagError?: string;
  onAddToBag: (variant: AddToBagVariant) => void;
  goToCartEnabled: boolean;
  onGoToCart: () => void;
  product: ProductBase | null | undefined;
}

export const AddToBagVariantSelect = ({
  variants,
  status,
  onAddToBag,
  goToCartEnabled,
  onGoToCart,
  addToBagError,
  product,
}: AddToBagVariantSelectProps) => {
  const { t } = useTranslation();
  const [selectedVariant, setSelectedVariant] = useState<AddToBagVariant | undefined>(undefined);
  const onSelect = useCallback((variant: AddToBagVariant) => setSelectedVariant(variant), []);

  const productName = useProductName(product);
  const onAddToBagClick = useCallback(() => (selectedVariant ? onAddToBag(selectedVariant) : undefined), [selectedVariant, onAddToBag]);

  const isSingleEmptyOption = useMemo(() => variants && variants.length === 1 && variants[0].title === '', [variants]);

  useEffect(() => {
    if (variants && isSingleEmptyOption) {
      onSelect(variants[0]);
      onAddToBag(variants[0]);
    }
  }, [isSingleEmptyOption, onAddToBag, onSelect, variants]);

  const options = useMemo(() => {
    if (status !== 'loadingVariants' && (!variants || variants.length === 0)) {
      return null;
    }
    if (status === 'loadingVariants' || !variants) {
      return (
        <div className={clsx(style.loading)}>
          <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.default} />
          <p className={clsx(globalStyle.textSmall, globalStyle.textBold)}>
            {t('LOC_ADD_TO_BAG_LOADING_SIZES', { size: selectedVariant?.title ?? '' })}
          </p>
        </div>
      );
    }
    if (isSingleEmptyOption) {
      return <div />;
    }
    return variants.map((variant) => (
      <AddToBagVariantOption key={variant.id} variant={variant} selected={!!selectedVariant && selectedVariant === variant} onSelect={onSelect} />
    ));
  }, [status, variants, isSingleEmptyOption, t, selectedVariant, onSelect]);

  const content = useMemo(() => {
    if (status === 'addToBagSuccess') {
      return (
        <div className={clsx(style.success)}>
          <div className={clsx(style.message)}>
            <SuccessCheck size={26} />
            <div>{t('LOC_ADD_TO_BAG_ADD_SUCCESS', { product: productName ?? selectedVariant?.title })}</div>
          </div>
          {goToCartEnabled ? (
            <Button outline onClick={onGoToCart} content={t('LOC_ADD_TO_BAG_GO_TO_CART')} cssProperties={{ width: '100%' }} />
          ) : null}
        </div>
      );
    }

    return (
      <>
        <h3>{productName}</h3>
        <div className={clsx(style.options)}>{options}</div>
        {addToBagError && status === 'addToBagError' ? (
          <div className={clsx(globalStyle.textSmall, globalStyle.textBold, globalStyle.textColorError)}>{addToBagError}</div>
        ) : null}
        <Button
          onClick={onAddToBagClick}
          content={
            status === 'addingToBag' ? (
              <div style={{ marginTop: '-1.5px', marginBottom: '-7px' }}>
                <Spinner size={SpinnerSize.small} color="#3e3e3e" positioning={SpinnerPositioning.default} hidePadding />
              </div>
            ) : (
              t('LOC_ADD_TO_BAG_BUTTON_TITLE')
            )
          }
          disabled={status === 'addingToBag' || status === 'loadingVariants' || !selectedVariant}
        />
      </>
    );
  }, [addToBagError, goToCartEnabled, onAddToBagClick, onGoToCart, options, productName, selectedVariant, status, t]);

  if (!options) {
    return null;
  }

  return <div className={clsx(style.root)}>{content}</div>;
};
